/**
 * Lenient parser for question-import payloads.
 *
 * Used by both the admin-console and teacher-app wrappers around
 * `ImportQuestionsDialog` so the supported input shapes stay aligned.
 *
 * Accepts:
 *   - `.json` — a single file with `{ questions: [...], images?: {...} }`.
 *   - `.zip`  — multiple `.json` files inside; each contributes one
 *     `ImportFile`. Useful for batched exports with embedded images.
 *
 * JSON parsing is lenient: external content-prep tools sometimes
 * double-escape backslashes (`\\\\mathrm{…}`), which standard `JSON.parse`
 * rejects. `lenientJsonParse` falls back to a character-level fix-up that
 * preserves the original string's intent.
 */

import JSZip from 'jszip';
import type { ImportFile } from './ImportQuestionsDialog';

/** Parse JSON leniently, fixing double-escaped backslashes that break JSON.parse(). */
function lenientJsonParse(raw: string): unknown {
    try { return JSON.parse(raw); } catch { /* fall through to fix */ }

    const result: string[] = [];
    let i = 0;
    while (i < raw.length) {
        if (raw[i] === '"') {
            result.push('"');
            i++;
            while (i < raw.length) {
                if (raw[i] === '\\') {
                    let bsCount = 0;
                    while (i < raw.length && raw[i] === '\\') { bsCount++; i++; }
                    if (i >= raw.length) { result.push('\\'.repeat(bsCount)); break; }
                    const next = raw[i];
                    if (next === '"') {
                        const pairs = Math.floor(bsCount / 2);
                        result.push('\\\\'.repeat(pairs));
                        if (bsCount % 2 === 1) { result.push('\\"'); i++; }
                        else break;
                    } else if ('bfnrt/u'.includes(next)) {
                        const pairs = Math.floor(bsCount / 2);
                        result.push('\\\\'.repeat(pairs));
                        if (bsCount % 2 === 1) { result.push('\\' + next); } else { result.push(next); }
                        i++;
                        if (next === 'u' && bsCount % 2 === 1) { result.push(raw.substring(i, i + 4)); i += 4; }
                    } else {
                        const pairs = Math.floor(bsCount / 2);
                        result.push('\\\\'.repeat(pairs));
                        result.push(next);
                        i++;
                    }
                } else if (raw[i] === '"') {
                    break;
                } else {
                    result.push(raw[i]);
                    i++;
                }
            }
            result.push('"');
            if (i < raw.length) i++;
        } else {
            result.push(raw[i]);
            i++;
        }
    }
    return JSON.parse(result.join(''));
}

/**
 * Parse a user-selected file into one or more `ImportFile` records.
 * `.json` → single record; `.zip` → one record per `.json` entry inside.
 *
 * Throws on:
 *   - ZIP with no valid JSON files.
 *   - JSON without a top-level `questions` array.
 *   - Underlying `JSON.parse` failure that even the lenient pass can't fix.
 */
export async function parseUploadedFile(file: File): Promise<ImportFile[]> {
    if (file.name.endsWith('.zip')) {
        const zip = await JSZip.loadAsync(file);
        const jsonFiles: ImportFile[] = [];

        for (const [name, entry] of Object.entries(zip.files)) {
            if (entry.dir || !name.endsWith('.json')) continue;
            const text = await entry.async('string');
            const parsed = lenientJsonParse(text) as Record<string, unknown>;
            if (parsed.questions && Array.isArray(parsed.questions)) {
                jsonFiles.push({
                    name: name.split('/').pop() || name,
                    questions: parsed.questions as Record<string, unknown>[],
                    images: parsed.images as ImportFile['images'],
                });
            }
        }

        if (jsonFiles.length === 0) {
            throw new Error('No valid JSON files with questions found in ZIP');
        }

        jsonFiles.sort((a, b) => a.name.localeCompare(b.name));
        return jsonFiles;
    }

    const text = await file.text();
    const parsed = lenientJsonParse(text) as Record<string, unknown>;
    if (!parsed.questions || !Array.isArray(parsed.questions)) {
        throw new Error('Invalid file format: missing questions array');
    }
    return [{
        name: file.name,
        questions: parsed.questions as Record<string, unknown>[],
        images: parsed.images as ImportFile['images'],
    }];
}
